package com.partejar.app.feature.gestantes.presentation

import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.partejar.app.core.ui.components.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.GestanteExamAnalysis
import com.partejar.app.feature.gestantes.data.GestanteExamResult

internal data class ExamCatalogChoice(val key: String, val label: String, val description: String? = null)

internal fun searchExamCatalog(options: List<ExamCatalogChoice>, query: String): List<ExamCatalogChoice> =
    options.filter { it.key == "other" || query.isBlank() || it.label.contains(query.trim(), ignoreCase = true) }

internal fun examCatalogSelectionError(analysisId: Long?, unitId: Long?, freeResult: Boolean?,
                                     analyses: List<GestanteExamAnalysis>, initial: GestanteExamResult?): String? {
    if (freeResult == true) return if (analysisId == null && unitId == null) null else "Outro resultado não pode usar o catálogo."
    if (analysisId == null) return if (freeResult == null && initial != null && initial.analysisId == null && unitId == null) null
        else "Selecione uma análise ou Outro resultado."
    val analysis = analyses.find { it.id == analysisId } ?: return "A análise não está disponível. Recarregue o catálogo."
    if (!analysis.active && initial?.analysisId != analysisId) return "Selecione uma análise ativa."
    if (unitId == null) return if (analysis.unitRequired) "Selecione uma unidade para a análise." else null
    val unit = analysis.units.find { it.id == unitId }
    return if (unit == null || (!unit.active && (initial?.analysisId != analysisId || initial.analysisUnitId != unitId)))
        "Selecione uma unidade permitida para a análise." else null
}

/** Feature-specific selector: fully constrained by the window, scrollable and accessible at 320dp. */
@Composable
internal fun ExamCatalogPicker(label: String, selectedKey: String?, selectedLabel: String?, options: List<ExamCatalogChoice>,
                               enabled: Boolean, searchable: Boolean = false, onSelect: (String) -> Unit) {
    val spacing = PartejarTheme.spacing
    val focus = LocalFocusManager.current
    var open by rememberSaveable { mutableStateOf(false) }
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
        Text(label, style = MaterialTheme.typography.labelLarge)
        PartejarCard(onClick = { focus.clearFocus(); open = true }, enabled = enabled, motionEnabled = true, modifier = Modifier.fillMaxWidth()) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(spacing.space8)) {
                Text(selectedLabel ?: "Selecionar ${label.lowercase()}", modifier = Modifier.weight(1f), style = MaterialTheme.typography.bodyLarge)
                Icon(PartejarIcons.Actions.next, "Selecionar ${label.lowercase()}")
            }
        }
    }
    if (open) {
        val visibility = remember { MutableTransitionState(false).apply { targetState = true } }
        var query by rememberSaveable { mutableStateOf("") }
        var pending by remember { mutableStateOf<String?>(null) }
        LaunchedEffect(visibility.isIdle, visibility.currentState) {
            if (visibility.isIdle && !visibility.currentState && !visibility.targetState) {
                pending?.let(onSelect)
                open = false
            }
        }
        LaunchedEffect(enabled) { if (!enabled) visibility.targetState = false }
        val dismiss = { focus.clearFocus(); visibility.targetState = false }
        Dialog(onDismissRequest = dismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
            Box(Modifier.fillMaxSize().windowInsetsPadding(WindowInsets.safeDrawing).imePadding(), contentAlignment = Alignment.Center) {
                PartejarVisibility(visibility) {
                    Surface(modifier = Modifier.fillMaxWidth(),
                        shape = MaterialTheme.shapes.extraLarge, color = MaterialTheme.colorScheme.surfaceContainerLow) {
                        Column(Modifier.padding(spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
                            Text("Selecionar ${label.lowercase()}", style = MaterialTheme.typography.titleLarge, modifier = Modifier.semantics { heading() })
                            if (searchable) PartejarTextField(query, { query = it }, label = "Buscar análise",
                                enabled = enabled && visibility.targetState, placeholder = "Digite o nome da análise")
                            Column(Modifier.weight(1f, fill = false).verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                                val filtered = searchExamCatalog(options, query)
                                if (filtered.none { it.key != "other" }) Text("Nenhuma análise encontrada para este tipo.",
                                    color = MaterialTheme.colorScheme.onSurfaceVariant, style = MaterialTheme.typography.bodyMedium)
                                filtered.forEach { choice ->
                                    PartejarCard(onClick = { pending = choice.key; dismiss() }, enabled = enabled && visibility.targetState,
                                        motionEnabled = true, modifier = Modifier.fillMaxWidth().semantics { selected = selectedKey == choice.key }) {
                                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(spacing.space8)) {
                                            RadioButton(selected = selectedKey == choice.key, onClick = null, enabled = enabled && visibility.targetState)
                                            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                                                Text(choice.label, style = MaterialTheme.typography.titleMedium)
                                                choice.description?.let { Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant) }
                                            }
                                        }
                                    }
                                }
                            }
                            PartejarButton("Cancelar", onClick = dismiss, enabled = visibility.targetState, variant = PartejarButtonVariant.TERTIARY,
                                motionEnabled = true, leadingIcon = { Icon(PartejarIcons.Actions.back, null) })
                        }
                    }
                }
            }
        }
    }
}
